import React, { useEffect }  from "react";
import { useState, useContext } from "react";
import { Card, Button, Col, Collapse, Form, Row} from "react-bootstrap";
import { AuthContext } from "../../Auth/AuthProvider";
import { MedicalLicense } from "../../Physician/Models/MedicalLicense";
import { abpnClient } from "../../../Services/AbpnClient";
import { StateWithCountryLookupItem } from "../../Shared/Models/StateWithCountryLookupItem";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { MedicalLicenseList } from "../../Shared/Components/MedicalLicenseList";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";

interface IProps {
    medicalLicenses: Array<MedicalLicense>;
    states: Array<StateWithCountryLookupItem>;
    examStartDate: string;
    isSectionOpen: boolean;
    requiresUnrestrictedLicense: boolean;
    onContinue: () => void;
    onOpen: () => void;
    disabled: boolean;
}

export function MedicalLicenseInformation(props: IProps) {
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [medicalLicenses, setMedicalLicenses] = useState<Array<MedicalLicense>>(props.medicalLicenses);

    const authContext = useContext(AuthContext);

    const validate = (medicalLicenses: Array<MedicalLicense>) : boolean => {
        medicalLicenses.forEach((medicalLicense: MedicalLicense) => {
            medicalLicense.validationMessage = new Array<string>();
            
            if (medicalLicense.medicalLicenseId === 0 && getDateWithOffset(medicalLicense.expirationDate) < new Date())
            {
                medicalLicense.validationMessage.push("Can not add expired licenses.");
            }
            if (!medicalLicense.licenseNumber?.trim()) {
                medicalLicense.validationMessage.push(" License number is required.");
            }
            if (!medicalLicense.stateId) {
                medicalLicense.validationMessage.push(" State is required");
            }
            if (!Date.parse(medicalLicense.expirationDate)){
                medicalLicense.validationMessage.push(" Expiration date invalid. Expected format: MM/DD/YYYY");
            }
            if (medicalLicense.validationMessage.length > 0){
                medicalLicense.isEditing = true;
            }
        })
        setMedicalLicenses(deepCopy(medicalLicenses));
        return !medicalLicenses.some(_ => _.validationMessage?.length > 0);
    }

    const saveAllMedicalLicenses = (medicalLicensesToUpdate: Array<MedicalLicense>) => {
        if (validate(medicalLicensesToUpdate))
        {
            setIsLoading(true);
            const url = "/api/Physician/SaveMedicalLicenses/";
            abpnClient.post<Array<MedicalLicense>>(url, medicalLicensesToUpdate, authContext).then(result => {
                result.forEach((license: MedicalLicense) => { license.isEditing = false; license.isNew = false });
                setMedicalLicenses(result);
                setIsLoading(false);
                props.onContinue();
            });
        }   
    }

    const addNewMedicalLicense = () => {
        var newMedicalLicense = new MedicalLicense();
        newMedicalLicense.isNew = true;
        newMedicalLicense.isEditing = true;
        var newList = deepCopy(medicalLicenses);
        newList.push(newMedicalLicense);
        setMedicalLicenses(newList);
    }

    const removeMedicalLicense = (medicalLicense: MedicalLicense) => {
        var newList = deepCopy(medicalLicenses.filter(x => x.medicalLicenseId !== medicalLicense.medicalLicenseId));
        setMedicalLicenses(newList);
    }

    const updateMedicalLicense = (medicalLicense: MedicalLicense) => {
        var updated = deepCopy(medicalLicenses)
        var index = updated.findIndex(_ => _.medicalLicenseId == medicalLicense.medicalLicenseId);
        updated[index] = medicalLicense;
        saveAllMedicalLicenses(updated);
    }

    const canAddNewLicense = () => {
        var newLicense = medicalLicenses.find(_ => _.isNew);
        return newLicense ? false : true;
    }

    const areLicensesValid = () => {
        return medicalLicenses.filter(_ => _.validationMessage?.length > 0).length === 0;
    }

    const saveAndContinue = () => {
        saveAllMedicalLicenses(medicalLicenses);
    }

    useEffect(() => {
        setOpenCollapse(props.isSectionOpen);
    }, [props.isSectionOpen]);

    useEffect(() => {
        if (openCollapse) props.onOpen();
    }, [openCollapse]);

    if (props.medicalLicenses == null) return <AbpnSpinner></AbpnSpinner>;
    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={() => {if (!props.disabled) setOpenCollapse(!openCollapse)}}>
                    <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="font-weight-bolder las la-angle-down"></i> Medical Licenses</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Row className="mt-3">
                        <Col xs={12} md={12} lg={12} xl={12}>
                            <Form noValidate>
                                <MedicalLicenseList medicalLicenses={medicalLicenses} states={props.states}
                                            handleRemove={(medicalLicense: MedicalLicense) => removeMedicalLicense(medicalLicense)}
                                            updateMedicalLicense={(medicalLicense: MedicalLicense) => updateMedicalLicense(medicalLicense)} />
                                    
                                    <Card.Text hidden={!props.requiresUnrestrictedLicense}>Full, unrestricted, active medical license required by September 1, {getDateWithOffset(props.examStartDate).getFullYear()}</Card.Text>
                                    <Card bsPrefix="border-0 pp-align-self-end mt-2">
                                        <Button variant="outline-pp-primary" disabled={!areLicensesValid() || !canAddNewLicense() } onClick={() => addNewMedicalLicense()}>Add License</Button>
                                    </Card>
                                    <Card bsPrefix="border-0 pp-align-self-end mt-3">
                                        {isLoading ? <AbpnSpinner /> : <Button variant="pp-primary" disabled={!areLicensesValid()} onClick={() => saveAndContinue()}>Continue</Button>}
                                    </Card>
                            </Form>
                        </Col>
                    </Row>
            </Collapse>
           </Card.Body>
        </Card>
    );

}